import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faBold,
  faH1,
  faItalic,
  faListOl,
  faListUl,
  faUnderline,
  type IconDefinition,
} from '@fortawesome/sharp-solid-svg-icons';
import { Editor, EditorState, Modifier, RichUtils } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { stateFromHTML } from 'draft-js-import-html';
import 'draft-js/dist/Draft.css';
import { Button } from 'eone-ui/buttons/button';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { SCView } from '../view';
import type { SCFormFieldProps } from './form_field';
import { SCFormField } from './form_field';

interface SCFormFieldRichTextEditorProps extends SCFormFieldProps {
  AutoFocus?: boolean;
  Icon?: IconProp;
  GlobalVariables?: { Label: string; Data: any }[];
  SourceColumns?: { Label: string; Data: any }[];
  Placeholder?: string;
}

const SCFormFieldRichTextEditor = ({
  label,
  name,
  required,
  disabled,
  rules,
  visible,
  defaultValue,
  GlobalVariables,
  Placeholder,
  SourceColumns,
}: SCFormFieldRichTextEditorProps) => {
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
  const { setValue, getValues } = useFormContext();
  const initialised = useRef(false);

  const toggleInlineStyle = useCallback(
    (style: string) => {
      setEditorState(RichUtils.toggleInlineStyle(editorState, style));
    },
    [editorState],
  );

  const toggleBlockType = useCallback(
    (blockType: string) => {
      setEditorState(RichUtils.toggleBlockType(editorState, blockType));
    },
    [editorState],
  );

  useEffect(() => {
    const state = stateFromHTML(getValues(name) ?? '');
    const newEditorState = EditorState.push(editorState, state, 'insert-characters');
    setEditorState(newEditorState);
    initialised.current = true;
  }, []);

  useEffect(() => {
    if (initialised.current == false) return;
    setValue(name, stateToHTML(editorState.getCurrentContent()));
  }, [editorState]);

  const insert = useCallback(
    (value: string) => {
      const currentContentState = editorState.getCurrentContent();
      const selectionState = editorState.getSelection();
      const newContentState = Modifier.insertText(currentContentState, selectionState, value);
      const newEditorState = EditorState.push(editorState, newContentState, 'insert-characters');
      setEditorState(newEditorState);
    },
    [editorState],
  );

  const controlButtons: {
    Icon?: IconDefinition;
    Label: string;
    MenuItems?: { Label: string; Data: any }[];
    OnClick?: any;
  }[] = useMemo(() => {
    const buttons = [
      {
        Label: '',
        Icon: faBold,
        OnClick: () => toggleInlineStyle('BOLD'),
      },
      {
        Label: '',
        Icon: faItalic,
        OnClick: () => toggleInlineStyle('ITALIC'),
      },
      {
        Label: '',
        Icon: faUnderline,
        OnClick: () => toggleInlineStyle('UNDERLINE'),
      },
      {
        Label: '',
        Icon: faH1,
        OnClick: () => toggleBlockType('header-one'),
      },
      {
        Label: '',
        Icon: faListUl,
        OnClick: () => toggleBlockType('unordered-list-item'),
      },
      {
        Label: '',
        Icon: faListOl,
        OnClick: () => toggleBlockType('ordered-list-item'),
      },
    ];
    if (GlobalVariables != undefined) {
      buttons.push({
        Label: 'Global Variables',
        OnClick: insert,
        MenuItems: GlobalVariables,
      });
    }
    if (SourceColumns != undefined) {
      buttons.push({
        Label: 'Source Columns',
        OnClick: insert,
        MenuItems: SourceColumns,
      });
    }
    return buttons;
  }, [toggleInlineStyle, toggleBlockType, SourceColumns, GlobalVariables]);

  const buttons = useMemo(() => {
    if (controlButtons === undefined || controlButtons === null || controlButtons.length === 0) {
      return null;
    }
    return controlButtons.map((button, index) => {
      return (
        <Button
          key={index}
          Label={button.Label}
          Icon={button.Icon}
          MenuItems={button.MenuItems}
          OnClick={button.OnClick}
        />
      );
    });
  }, [controlButtons]);

  const handleKeyCommand = useCallback((command: string, editorState: EditorState) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      setEditorState(newState);
      return 'handled';
    }
    return 'not-handled';
  }, []);

  return (
    <SCFormField
      visible={visible}
      label={label}
      name={name}
      disabled={disabled}
      required={required || !!rules?.required}
      rules={rules}
      defaultValue={defaultValue}
    >
      {() => {
        return (
          <SCView flex flexRow wFull justifyEnd itemsEnd>
            <SCView flex flex1>
              <SCView wFull>
                {label && (
                  <p style={{ margin: 0, marginBottom: 6, color: '#333' }}>
                    {label}
                    {required && '*'}
                  </p>
                )}
                <div style={{ border: '1px solid #ccc' }}>
                  <div
                    style={{
                      display: 'flex',
                      borderBottom: '1px solid #ccc',
                      flexDirection: 'row',
                      gap: 10,
                      padding: 10,
                    }}
                  >
                    {buttons}
                  </div>
                  <div
                    style={{
                      paddingLeft: 10,
                      paddingRight: 10,
                      paddingTop: 10,
                      minHeight: 100,
                      maxHeight: 200,
                      overflow: 'scroll',
                    }}
                  >
                    <Editor
                      editorState={editorState}
                      onChange={setEditorState}
                      handleKeyCommand={handleKeyCommand}
                      placeholder={Placeholder ?? 'Type here'}
                    />
                  </div>
                </div>
              </SCView>
            </SCView>
          </SCView>
        );
      }}
    </SCFormField>
  );
};

export { SCFormFieldRichTextEditor };
export type { SCFormFieldRichTextEditorProps };
